import {
  Box,
  Checkbox,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import { KeyboardArrowDown, SearchOutlined } from "@mui/icons-material";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

export interface MultiSelectListOption {
  label: string;
  value: string;
}

interface MultiSelectSearchDropdownProps {
  options: MultiSelectListOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  displayValue: string;
  placeholder?: string;
  searchInputPlaceholder?: string;
  zeroOptionsMessage?: string;
  isDisabled?: boolean;
}

const formatOptionLabel = (label: string): string =>
  label
    .split(" ")
    .map((part) => (part ? part[0]?.toUpperCase() + part.slice(1) : ""))
    .join(" ");

const TRIGGER_BORDER_RADIUS = "7px";

const MultiSelectSearchDropdown = ({
  options,
  selectedValues,
  onChange,
  displayValue,
  placeholder = "Car Maker",
  searchInputPlaceholder = "Search...",
  zeroOptionsMessage = "No options available",
  isDisabled = false,
}: MultiSelectSearchDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const preservedScrollTopRef = useRef(0);

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (isOpen) {
        setIsOpen(false);
        setSearchValue("");
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setSearchValue("");
    preservedScrollTopRef.current = 0;
    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!listScrollRef.current || !isOpen) {
      return;
    }
    listScrollRef.current.scrollTop = preservedScrollTopRef.current;
  }, [selectedValues, isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) {
      return options;
    }
    const query = searchValue.toUpperCase();
    return options.filter((item) => item.label.toUpperCase().includes(query));
  }, [options, searchValue]);

  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const openDropdown = () => {
    if (isDisabled) {
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handleToggleOption = (option: MultiSelectListOption) => {
    if (listScrollRef.current) {
      preservedScrollTopRef.current = listScrollRef.current.scrollTop;
    }
    if (selectedValues.includes(option.value)) {
      onChange(selectedValues.filter((value) => value !== option.value));
      return;
    }
    onChange([...selectedValues, option.value]);
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    }
    if (event.key === "Escape") {
      setIsOpen(false);
      setSearchValue("");
    }
  };

  return (
    <Box ref={containerRef} position="relative" width="100%">
      <Box
        position="relative"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        onClick={openDropdown}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Input
          placeholder={placeholder}
          value={displayValue}
          isReadOnly
          isDisabled={isDisabled}
          cursor={isDisabled ? "not-allowed" : "pointer"}
          borderRadius={TRIGGER_BORDER_RADIUS}
          borderColor="gray.300"
          size="sm"
          bg="white"
          pr={10}
          pointerEvents="none"
        />
        <Icon
          position="absolute"
          right={2}
          top="50%"
          transform="translateY(-50%)"
          color="blue.400"
          zIndex={1}
          pointerEvents="none"
        >
          <KeyboardArrowDown
            fontSize="small"
            style={{
              transform: isOpen ? "rotate(180deg)" : undefined,
              transition: "transform 0.15s ease",
            }}
          />
        </Icon>
      </Box>

      {isOpen && !isDisabled && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={20}
          bg="white"
          border="1px solid"
          borderColor="gray.300"
          borderRadius={TRIGGER_BORDER_RADIUS}
          boxShadow="md"
          overflow="hidden"
        >
          <Box
            px={2}
            pt={2}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none" h="100%">
                <Icon color="gray.400" boxSize={4}>
                  <SearchOutlined fontSize="small" />
                </Icon>
              </InputLeftElement>
              <Input
                ref={searchInputRef}
                placeholder={searchInputPlaceholder}
                value={searchValue}
                type="text"
                onChange={(event) => setSearchValue(event.target.value)}
                borderRadius={TRIGGER_BORDER_RADIUS}
                borderColor="gray.300"
                pl={9}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setIsOpen(false);
                    setSearchValue("");
                  }
                }}
              />
            </InputGroup>
          </Box>

          <Box ref={listScrollRef} maxH="260px" overflowY="auto" p={2}>
            {options.length === 0 ? (
              <Box textAlign="center" fontSize="sm" py={4} color="gray.500">
                {zeroOptionsMessage}
              </Box>
            ) : filteredOptions.length === 0 ? (
              <Box textAlign="center" fontSize="sm" py={4} color="gray.500">
                No matching car makers
              </Box>
            ) : (
              <UnorderedList listStyleType="none" m={0}>
                {filteredOptions.map((option) => {
                  const isSelected = selectedValueSet.has(option.value);
                  return (
                    <ListItem
                      key={option.value}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleOption(option);
                      }}
                      px={2}
                      py={1.5}
                      mb={1}
                      cursor="pointer"
                      _hover={{ backgroundColor: "gray.50" }}
                      display="flex"
                      alignItems="center"
                      borderRadius={TRIGGER_BORDER_RADIUS}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <Checkbox
                        isChecked={isSelected}
                        pointerEvents="none"
                        colorScheme="blue"
                        mr={2}
                        flexShrink={0}
                      />
                      <Text fontSize="sm" noOfLines={1}>
                        {formatOptionLabel(option.label)}
                      </Text>
                    </ListItem>
                  );
                })}
              </UnorderedList>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MultiSelectSearchDropdown;
